import React from "react";
import { X } from "lucide-react";
import { formatDateTimeMedium } from "../utils/dateFormatter";
import { getImageUrl } from "../utils/imageHelper";

const ItemDetailsModal = ({ item, onClose }) => {
  if (!item) return null;

  // Date formatting now handled by utils/dateFormatter with IST timezone
  const formatDate = formatDateTimeMedium;

  const formatValue = (value) => {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value;
  };

  const Field = ({ label, value, fullWidth = false }) => (
    <div className={`${fullWidth ? "col-span-2" : ""}`}>
      <dt className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">{formatValue(value)}</dd>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="pt-3">
      <h3 className="text-sm font-bold text-[#6b4226] uppercase tracking-wide mb-2.5">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-[#fffaf5]">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{item.name}</h2>
            {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-600 transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] rounded-full p-1 cursor-pointer"
            title="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="space-y-4">
            {/* Image & Basic Information - Side by Side */}
            <div className="flex flex-col md:flex-row gap-8 pb-4 border-gray-200">
              {/* Image */}
              {item.image_url && (
                <div className="flex-shrink-0">
                  <h3 className="text-sm font-bold text-[#6b4226] uppercase tracking-wide mb-2.5">Image</h3>
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-full max-w-[224px] h-auto rounded-lg shadow-md object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}

              {/* Basic Information */}
              <div className="flex-1">
                <h3 className="text-sm font-bold text-[#6b4226] uppercase tracking-wide mb-2.5">Basic Information</h3>
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <Field label="ID" value={item.id} />
                  <Field label="Rista ID" value={item.rista_id} />
                  <Field label="SKU Code" value={item.sku_code} />
                  <Field label="Type" value={item.type} />
                  <Field label="Status" value={item.status} />
                  <Field label="Item Nature" value={item.item_nature} />
                  <Field label="Calories" value={item.calories} />
                  <Field label="Measuring Unit" value={item.measuring_unit} />
                  <Field label="Price Includes Tax" value={item.is_price_includes_tax} />
                  <Field label="Deny Discount" value={item.deny_discount} />
                </dl>
              </div>
            </div>

            {/* Nutritional Information */}
            {(item.calorie_serve ||
              item.protein ||
              item.fat ||
              item.carbs ||
              item.sugar ||
              item.sat_fat ||
              item.trans_fat ||
              item.sodium ||
              item.fiber) && (
              <Section title="Nutritional Information">
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {item.calorie_serve && (
                    <Field label="Calories per Serve" value={`${item.calorie_serve} kcal`} />
                  )}
                  {item.protein && <Field label="Protein" value={`${item.protein}g`} />}
                  {item.fat && <Field label="Fat" value={`${item.fat}g`} />}
                  {item.carbs && <Field label="Carbohydrates" value={`${item.carbs}g`} />}
                  {item.sugar && <Field label="Sugar" value={`${item.sugar}g`} />}
                  {item.sat_fat && <Field label="Saturated Fat" value={`${item.sat_fat}g`} />}
                  {item.trans_fat && <Field label="Trans Fat" value={`${item.trans_fat}g`} />}
                  {item.sodium && <Field label="Sodium" value={`${item.sodium}mg`} />}
                  {item.fiber && <Field label="Dietary Fiber" value={`${item.fiber}g`} />}
                </dl>
              </Section>
            )}

            {/* Additional Item Details */}
            {(item.health_benefits || item.suitable_for || item.food_pairing || item.mood || item.dietary_type) && (
              <Section title="Additional Details">
                <dl className="space-y-3">
                  {item.health_benefits && (
                    <Field label="Health Benefits" value={item.health_benefits} fullWidth />
                  )}
                  {item.suitable_for && (
                    <Field label="Suitable For" value={item.suitable_for} fullWidth />
                  )}
                  {item.dietary_type && (
                    <Field label="Dietary Type" value={item.dietary_type} fullWidth />
                  )}
                  {item.food_pairing && (
                    <Field label="Food Pairing" value={item.food_pairing} fullWidth />
                  )}
                  {item.mood && <Field label="Mood" value={item.mood} fullWidth />}
                </dl>
              </Section>
            )}

            {/* Item Classification */}
            {(item.is_signature || item.is_healthy) && (
              <Section title="Item Classification">
                <div className="flex flex-wrap gap-2">
                  {item.is_signature && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-amber-100 text-amber-800">
                      Signature Item
                    </span>
                  )}
                  {item.is_healthy && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                      Healthy Option
                    </span>
                  )}
                </div>
              </Section>
            )}

            {/* Store-Specific Pricing */}
            {item.store_items && item.store_items.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <Section title={`Store-Specific Pricing (${item.store_items.length} stores)`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Store</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Branch Code</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">City</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Price</th>
                        <th className="px-3 py-2 text-center font-medium text-gray-600">Available</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {item.store_items.map((storeItem) => (
                        <tr key={storeItem.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900 font-medium">
                            {storeItem.store?.store_name}
                          </td>
                          <td className="px-3 py-2 text-gray-700 font-mono text-xs">
                            {storeItem.store?.branch_code || "—"}
                          </td>
                          <td className="px-3 py-2 text-gray-700">{storeItem.store?.city}</td>
                          <td className="px-3 py-2 text-right">
                            <span className="font-semibold text-gray-900">
                              ₹{storeItem.price.toFixed(2)}
                            </span>
                            {storeItem.price !== item.price && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Base: ₹{item.price.toFixed(2)})
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                storeItem.available
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {storeItem.available ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Store-specific pricing overrides the base item price (₹
                    {item.price.toFixed(2)}). Stores without specific pricing will use the base
                    price.
                  </p>
                </div>
              </Section>
              </div>
            )}

            {/* Category Information */}
            <div className="pt-4 border-t border-gray-200">
              <Section title="Category Information">
                <dl className="grid grid-cols-2 gap-3">
                  <Field label="Category" value={item.category?.name} />
                  <Field label="Category ID" value={item.category_id} />
                </dl>
              </Section>
            </div>

            {/* Tags */}
            {item.tags && item.tags.length > 0 && (
              <Section title={`Tags (${item.tags.length})`}>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag.id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                    >
                      {tag.tag_name}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {/* Charges */}
            {item.charges && item.charges.length > 0 && (
              <Section title={`Charges (${item.charges.length})`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Charge Name
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {item.charges.map((charge) => (
                        <tr key={charge.id}>
                          <td className="px-3 py-2 text-gray-900">{charge.charge_name}</td>
                          <td className="px-3 py-2 text-gray-900">₹{charge.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Tax Types */}
            {item.tax_types && item.tax_types.length > 0 && (
              <Section title={`Tax Types (${item.tax_types.length})`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Tax Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Tax Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {item.tax_types.map((tax) => (
                        <tr key={tax.id}>
                          <td className="px-3 py-2 text-gray-900">{tax.tax_name}</td>
                          <td className="px-3 py-2 text-gray-900">
                            {tax.tax_rate ? `${tax.tax_rate}%` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Option Sets */}
            {item.option_sets && item.option_sets.length > 0 && (
              <Section title={`Option Sets (${item.option_sets.length})`}>
                <div className="space-y-3">
                  {item.option_sets.map((optionSet) => (
                    <div
                      key={optionSet.id}
                      className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                    >
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {optionSet.option_set_name}
                      </h4>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p>
                          Min Selection: {formatValue(optionSet.min_selection)} | Max Selection:{" "}
                          {formatValue(optionSet.max_selection)}
                        </p>
                        {optionSet.options_data && (
                          <details className="mt-2">
                            <summary className="cursor-pointer font-medium text-[#6b4226] hover:underline">
                              View Options Data
                            </summary>
                            <pre className="mt-2 text-xs bg-white p-2 rounded border border-gray-200 overflow-x-auto">
                              {JSON.stringify(optionSet.options_data, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Schedule Prices */}
            {item.schedule_prices && item.schedule_prices.length > 0 && (
              <Section title={`Schedule Prices (${item.schedule_prices.length})`}>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Schedule Name
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Price</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Time</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Days of Week
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {item.schedule_prices.map((schedule) => (
                        <tr key={schedule.id}>
                          <td className="px-3 py-2 text-gray-900">{schedule.schedule_name}</td>
                          <td className="px-3 py-2 text-gray-900">₹{schedule.price.toFixed(2)}</td>
                          <td className="px-3 py-2 text-gray-900">
                            {schedule.start_time && schedule.end_time
                              ? `${schedule.start_time} - ${schedule.end_time}`
                              : "—"}
                          </td>
                          <td className="px-3 py-2 text-gray-900">
                            {schedule.days_of_week && schedule.days_of_week.length > 0
                              ? schedule.days_of_week.join(", ")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Raw Data */}
            {item.raw_data && (
              <Section title="Raw Data">
                <details>
                  <summary className="cursor-pointer font-medium text-[#6b4226] hover:underline">
                    View Raw JSON Data
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto max-h-96">
                    {JSON.stringify(item.raw_data, null, 2)}
                  </pre>
                </details>
              </Section>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 mt-auto">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#6b4226] text-white rounded-lg hover:bg-[#58341f] transition focus:outline-none focus:ring-2 focus:ring-[#6b4226] focus:ring-offset-2"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailsModal;
